/**
 * Derived grid sizing, kept INCREMENTAL — the two O(rows × cols) passes that
 * windowing alone does not fix (docs/10 §3, "also required, or windowing
 * under-delivers").
 *
 * Windowing takes the DOM out of the per-edit cost (73 ms → 0.6 ms at 10k
 * rows). It does nothing about the two full passes over every cell that the
 * grid ran on EVERY cell change:
 *
 *   1. the F-26 run-width pass (`GridTable`'s `colWidths`), which re-walked
 *      every cell of every column to find the widest unwrappable run, and
 *   2. row-height prediction (`core/measure.ts`), which windowing ADDS.
 *
 * Left as full passes, those two are the 964 ms mount at 50k rows, and they
 * would put ~40 ms back into every keystroke and every arriving F-70
 * translation batch — handing back most of what windowing just won. Both are
 * therefore incremental here: the first pass over a column/row is O(n), and
 * every edit after it costs O(damaged cells).
 *
 * Both caches are DERIVED CLIENT STATE and never written to the CRDT (docs/04
 * §5: only manual F-14 resizes and the F-36 element ratchet write sizes, and
 * they act as floors that arrive here as `minHeight` / `manualWidth`). Every
 * client recomputes identically from `v` + `colMeta.width` + the font.
 *
 * Pure: no DOM, no React, no Yjs. The caller supplies the measurement
 * functions, which is what makes this unit-testable in node.
 */
import type { MeasureText } from "./measure.js";
import { predictRowHeight } from "./measure.js";
import { maxRunWidth } from "./table.js";

/* ------------------------------------------------------------------ */
/* F-26 run widths — per-column running max (docs/09 §3)              */
/* ------------------------------------------------------------------ */

interface ColumnRuns {
  /** rowId → widest unwrappable run in that cell (board px). */
  readonly runs: Map<string, number>;
  /** Cached max over `runs`; null = dirty, recompute lazily. */
  max: number | null;
}

/**
 * Per-column running max of the widest unwrappable run (docs/09 §3's
 * `ColumnRunTracker` shape). A cell edit is O(1) — unless it SHRANK the
 * column's current maximum, which is the one case that needs a rescan, and
 * that rescan is over cached numbers (no re-measurement).
 *
 * A column is only tracked once `build()` has seen every row of it. `update()`
 * deliberately no-ops for an unbuilt column: a lone `update()` would create a
 * column entry holding exactly one row, and `maxOf()` would then confidently
 * report that single row's run as the column's maximum — collapsing the column
 * to fit one cell. Build-then-update is the invariant.
 *
 * THE ROW SET IS TRACKED EXPLICITLY (`rows`) because the damage path cannot
 * carry it. A cell edit reaches `update()`; a row APPEARING or VANISHING does
 * not touch a cell at all (docs/07 C4 keeps a deleted row's cells as orphans,
 * and undoing the delete restores only `rowMeta`), so `syncRows` is the only
 * thing that can tell this class which rows are live. Every tracked column is
 * built against the same sheet-wide row list, so one shared set serves them all.
 */
export class ColumnRunTracker {
  private readonly cols = new Map<string, ColumnRuns>();
  /** The live row set the tracked columns are accounted against (`syncRows`). */
  private rows: ReadonlySet<string> = new Set();

  has(colId: string): boolean {
    return this.cols.has(colId);
  }

  /** Seed a column from every row. O(rows), once per column. */
  build(colId: string, rowIds: Iterable<string>, runOf: (rowId: string) => number): void {
    const runs = new Map<string, number>();
    let max = 0;
    for (const rowId of rowIds) {
      const w = runOf(rowId);
      if (w > 0) runs.set(rowId, w); // empty cells are the common case — don't store 0s
      if (w > max) max = w;
    }
    this.cols.set(colId, { runs, max });
  }

  /** Record one cell's run width (the damage path). No-op if unbuilt. */
  update(colId: string, rowId: string, run: number): void {
    const col = this.cols.get(colId);
    if (col === undefined) return; // not built yet — build() will read it
    const prev = col.runs.get(rowId) ?? 0;
    if (prev === run) return;
    if (run > 0) col.runs.set(rowId, run);
    else col.runs.delete(rowId);
    if (col.max === null) return; // already dirty
    if (run > col.max) col.max = run;
    // The edit shrank the cell that WAS the maximum: the new max is unknown.
    else if (prev >= col.max && run < prev) col.max = null;
  }

  /** Widest run in a column (0 when unbuilt/empty). Rescans only when dirty. */
  maxOf(colId: string): number {
    const col = this.cols.get(colId);
    if (col === undefined) return 0;
    if (col.max === null) {
      let max = 0;
      for (const w of col.runs.values()) if (w > max) max = w;
      col.max = max;
    }
    return col.max;
  }

  /**
   * Reconcile the tracked columns with the sheet's LIVE rows — BOTH directions,
   * because a row entering or leaving the sheet writes no cell and so generates
   * no damage the `update()` path could see.
   *
   * VANISHED rows must be dropped. docs/07 C4 says deleting a row leaves its
   * cells behind as ORPHANED entries (swept later, by snapshot compaction), and
   * those never generate cell-change damage — so without this a deleted row's
   * long URL would hold its column wide forever. The old full pass got this for
   * free by iterating live `rowIds`.
   *
   * APPEARED rows must be re-read, and this half is just as load-bearing: undo
   * of a row delete restores ONLY the `rowMeta` entry (C4 kept the cells all
   * along), so no cell is written, `update()` never fires, and the row's run
   * would stay dropped forever — F-26 would quietly stop accounting for it while
   * a client that loaded the board later, and therefore BUILT from the live rows,
   * would compute a wider column. Since `colEdges` are what cell-anchored pins,
   * contained elements and selection rects resolve against (docs/04 §5: "derived
   * client state, recomputed identically by every client"), that divergence puts
   * the same pin at two different board positions on two clients.
   *
   * Cost is O(vanished + appeared × tracked columns), so the steady state is
   * free and a bulk import pays once per chunk for its own new rows — never
   * O(rows). An appeared row whose cell the damage path also reported re-reads
   * it once more; `update()` no-ops on an unchanged value and `runOf` is
   * memoized per string, so that costs a doc read, not a measurement.
   */
  syncRows(rowIds: Iterable<string>, runOf: (colId: string, rowId: string) => number): void {
    const live = rowIds instanceof Set ? (rowIds as ReadonlySet<string>) : new Set(rowIds);
    for (const rowId of this.rows) {
      if (live.has(rowId)) continue;
      for (const col of this.cols.values()) {
        const dropped = col.runs.get(rowId);
        if (dropped === undefined) continue; // never held a run (empty cell)
        col.runs.delete(rowId);
        if (col.max !== null && dropped >= col.max) col.max = null;
      }
    }
    for (const rowId of live) {
      if (this.rows.has(rowId)) continue;
      // `update` no-ops for an unbuilt column — build() will read the row.
      for (const colId of this.cols.keys()) this.update(colId, rowId, runOf(colId, rowId));
    }
    this.rows = live;
  }

  /** Forget a column entirely (deleted, or the sheet changed). */
  deleteColumn(colId: string): void {
    this.cols.delete(colId);
  }

  /** Columns currently tracked — the caller prunes deleted ones. */
  trackedColumns(): Iterable<string> {
    return this.cols.keys();
  }
}

/* ------------------------------------------------------------------ */
/* Row heights — HYBRID: measured is truth, prediction fills the rest  */
/* ------------------------------------------------------------------ */

interface RowHeightEntry {
  /** Stored floor this height was derived with (F-14 manual / F-36 ratchet). */
  min: number;
  /** Effective row height in whole board px. */
  h: number;
  /** True once a real mounted row reported this height (docs/10 §2c "truth"). */
  measured: boolean;
}

/**
 * Row heights for EVERY row, whether or not it has a DOM node — the F-10
 * constraint (a) height source, in the HYBRID shape decided in docs/10 §2c.
 *
 * - **A measured height is truth.** Any row that has ever mounted stores what
 *   it really rendered at.
 * - **Prediction fills the rest**, lazily, and is cached.
 * - The height is applied as the mounted cell's `min-height`, never `height`,
 *   so F-26 "content never crops" holds and a wrong prediction SELF-HEALS on
 *   the pass after the row first mounts.
 *
 * WHY THE CORRECTION CANNOT OSCILLATE: `predictRowHeight` ceils, so the
 * `min-height` is ≥ the row's fractional intrinsic height. The browser's row
 * track is therefore `max(prediction, intrinsic)` ≥ prediction — a measurement
 * can only ever be LARGER than what we predicted (the Thai/Khmer ±1 line case),
 * never smaller. Feeding it back raises the floor, the next layout reports the
 * same number, and it converges in exactly one extra pass. This is also why
 * the standard objection to estimate-and-correct ("positions shift as rows
 * mount, so pins jump") does not bite: on the measured realistic corpus the
 * correction is a no-op for 100 % of rows (docs/10 §2c).
 *
 * INVALIDATION is content-addressed in effect, but driven imperatively so it
 * stays O(damage) instead of O(rows):
 *   - a cell edit → `invalidateRow` from the grid's cell-damage listener;
 *   - a column width or font change → `setSignature`, which drops everything
 *     (every row re-wraps);
 *   - a changed stored floor (F-14 resize, F-36 ratchet) → detected by
 *     `heightOf` comparing `min`, because those arrive as a structure bump
 *     rather than as cell damage.
 */
export class RowHeightCache {
  private readonly entries = new Map<string, RowHeightEntry>();
  private signature = "";

  constructor(private readonly measure: MeasureText) {}

  /**
   * Declare what the cached heights are valid FOR: the column widths and the
   * font version. A change drops every entry (both predictions and
   * measurements — the rows re-wrap, so a measured height is just as stale).
   * Returns true if the cache was dropped.
   */
  setSignature(signature: string): boolean {
    if (this.signature === signature) return false;
    this.signature = signature;
    this.entries.clear();
    return true;
  }

  /** Forget one row's height — its content changed (the damage path). */
  invalidateRow(rowId: string): void {
    this.entries.delete(rowId);
  }

  /**
   * The row's height, predicting and caching on a miss. `values` is a THUNK so
   * a cache hit never touches the doc: reading every row's cells on every pass
   * is exactly the O(rows × cols) cost this class exists to avoid.
   */
  heightOf(
    rowId: string,
    min: number,
    values: () => readonly string[],
    colWidths: readonly number[],
  ): number {
    const hit = this.entries.get(rowId);
    // A changed floor re-predicts: the stored min is an input to the height,
    // and it changes without any cell damage (F-14 resize, F-36 ratchet).
    if (hit !== undefined && hit.min === min) return hit.h;
    const h = predictRowHeight(values(), colWidths, min, this.measure);
    this.entries.set(rowId, { min, h, measured: false });
    return h;
  }

  /**
   * Record what a MOUNTED row actually rendered at. Returns true when this
   * changed the row's height (i.e. the prediction was wrong and the caller must
   * re-derive the edges).
   *
   * Ignored for a row with no entry: the height must be predicted first, or a
   * measurement would be recorded against an unknown floor and survive a
   * `min` change it knows nothing about.
   */
  setMeasured(rowId: string, height: number): boolean {
    const hit = this.entries.get(rowId);
    if (hit === undefined) return false;
    if (hit.h === height) {
      hit.measured = true;
      return false;
    }
    hit.h = height;
    hit.measured = true;
    return true;
  }

  /** True once this row's height came from a real mounted row. */
  isMeasured(rowId: string): boolean {
    return this.entries.get(rowId)?.measured ?? false;
  }

  /** Drop rows that no longer exist (deleted, or sheet switch). */
  retainRows(rowIds: ReadonlySet<string>): void {
    for (const rowId of this.entries.keys()) {
      if (!rowIds.has(rowId)) this.entries.delete(rowId);
    }
  }

  /** Entries held — tests and diagnostics. */
  get size(): number {
    return this.entries.size;
  }
}

/**
 * Cache signature for a set of column widths + the font (`FONT_VERSION`).
 * Content-addressed, so invalidation is automatic: a column resize changes the
 * widths, a webfont would change the font version, and either drops the
 * heights that were derived under the old geometry.
 */
export function heightSignature(colWidths: readonly number[], fontVersion: string): string {
  return `${fontVersion}|${colWidths.join(",")}`;
}

/**
 * Widest unwrappable run of a cell's text (F-26), memoized per exact string.
 * Content-addressed: editing a cell changes the text, so the key changes and
 * the stale entry is simply never read again (docs/09 §2's argument).
 */
export function createRunWidthCache(measure: MeasureText): (text: string) => number {
  const cache = new Map<string, number>();
  return (text: string): number => {
    const hit = cache.get(text);
    if (hit !== undefined) return hit;
    const w = maxRunWidth(text, measure);
    if (cache.size > 20_000) cache.clear();
    cache.set(text, w);
    return w;
  };
}
